/**
 * src/pages/public/Unsubscribe.jsx — a client stops a studio's aftercare emails.
 *
 * Reached from the "Unsubscribe" link in every aftercare email (see
 * api/src/services/aftercare.js). No login: the token in the URL is the only
 * credential, like ConsentSign.jsx and JoinInvite.jsx.
 *
 * Opening the page changes nothing; the client has to press the button. Mail
 * security scanners open every link in an email, so a page that unsubscribed
 * on load would opt people out who never clicked. Mail providers' one-click
 * unsubscribe doesn't come through here at all — web/src/worker.js forwards
 * their POST straight to the API.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { useNoindex } from '../../lib/useNoindex.js';
import { ErrorBanner, Loader } from '../../components/ui.jsx';
import { Button, PortalHeader } from '../../components/ui/index.js';
import PortalFooter from './PortalFooter.jsx';
import './portal.css';

export default function Unsubscribe() {
  useNoindex();
  const { token } = useParams();
  const { data, error, loading } = useApi(`/api/unsubscribe/${token}`);

  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const unsubscribe = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      await api.post(`/api/unsubscribe/${token}`, {});
      setDone(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-page" style={{ maxWidth: 460 }}>
      <main className="portal-main">
        <Loader loading={loading} error={error}>
          {data ? (
            <>
              <PortalHeader studioName={data.studioName} />
              <section className="portal-panel" style={{ textAlign: 'center' }}>
                {done || data.unsubscribed ? (
                  <>
                    <h2 className="portal-panel-title">You&rsquo;re unsubscribed</h2>
                    <p>
                      You won&rsquo;t get any more aftercare emails from {data.studioName}. If you
                      change your mind, ask the studio to turn them back on.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="portal-panel-title">Stop aftercare emails?</h2>
                    <p>
                      {data.studioName} sends aftercare tips and check-ins after your appointments.
                      You can stop them here.
                    </p>
                    <ErrorBanner>{submitError}</ErrorBanner>
                    <Button variant="primary" onClick={unsubscribe} disabled={busy}>
                      {busy ? 'Unsubscribing…' : 'Unsubscribe'}
                    </Button>
                  </>
                )}
              </section>
            </>
          ) : null}
        </Loader>
      </main>
      <PortalFooter />
    </div>
  );
}
